<template>
	<div
		:class="[
			variant ? `toggle toggle--${variant}` : 'toggle',
			isLabelBefore ? 'is-label-before' : ''
		]"
		:id="uid"
		:variant="variant"
	>
		<span class="toggle__container">
			<input
				aria-label="select the toggle to filter the list"
				class="toggle__input"
				name="toggle"
				type="checkbox"
				:checked="shouldBeChecked"
				:disabled="disabled"
				:icon="icon"
				:id="`${uid}_toggle`"
				:label="label"
				@change="changeInput"
			/>
			<label class="toggle__slider" :for="`${uid}_toggle`">
				<i :class="['icon', icon]"></i>

				<span v-if="variant === 'condensed'" class="option1"
					><slot name="true-value"></slot
				></span>
				<span v-if="variant === 'condensed'" class="option2"
					><slot name="false-value"></slot
				></span>
			</label>
			<label
				class="toggle__label-inside"
				:for="`${uid}_toggle`"
				v-if="variant !== 'condensed'"
			>
				<span class="option1"><slot name="true-value"></slot></span>
				<span class="option2"><slot name="false-value"></slot></span>
			</label>
		</span>
		<label :class="['toggle__label']" :for="`${uid}_toggle`" v-if="label">
			{{ label }}
		</label>
	</div>
</template>

<script>
export default {
	name: 'Toggle',
	model: {
		prop: 'value',
		event: 'change'
	},
	props: {
		icon: {
			type: String,
			default: ''
		},
		label: {
			type: String,
			default: ''
		},
		value: {
			type: Boolean,
			default: false
		},
		isLabelBefore: {
			type: Boolean,
			default: false
		},
		disabled: {
			type: Boolean,
			default: false
		},
		variant: {
			type: String,
			default: 'label-in',
			validator: function(value) {
				return ['condensed', 'label-in', ''].includes(value);
			}
		}
	},
	computed: {
		shouldBeChecked() {
			return this.value;
		}
	},
	methods: {
		changeInput(event) {
			this.$emit('change', event.target.checked);
		}
	}
};
</script>

<style lang="scss">
/**
    * [Description]
    * 
    * Global Switches
    * Can use SCSS variables = TRUE
    *
    * All switches styles and variations are included here.
    * 
    * [Table of Contents]
    *
    * BASE
 */

/*------------------------------------*\
  #BASE
\*------------------------------------*/
@import '@/scss/5-objects/_form.scss';

.toggle {
	@include create-check-element('.toggle__input', 56px, 24px, 28px);
	position: relative;

	&__container {
		position: relative;
	}

	&.toggle--label-in {
		position: relative;
		@include create-check-element('.toggle__input', 68px, 24px, 28px);
		.toggle__input {
			~ .toggle__label-inside {
				display: flex;
				justify-content: space-between;
				position: absolute;
				padding: 12px;
				top: 50%;
				transform: translateY(-50%);
				width: 68px; //should match create-check-element mixin width value
				font-size: $switch-font-size;
				text-transform: uppercase;
				margin-left: 0;
				.option1 {
					@include quick-animation;
					opacity: 0;
				}
				.option2 {
					@include quick-animation;
				}
			}

			&:checked {
				border: $form-input-border--checked;
				background-color: $accent;

				+ .toggle__slider {
					left: 42px; //create-check-element mixin width value minus slider width
				}
				~ .toggle__label-inside {
					.option1 {
						opacity: 1;
					}
					.option2 {
						opacity: 0;
					}
				}
			}
			&:hover::after {
				border: $form-input-border-hover;
			}
		}
	}

	&.is-label-before {
		.toggle__container {
			order: 1;
			position: relative;
		}
	}

	&__slider {
		position: absolute;
		cursor: pointer;
		left: 0;
		top: -1px;
		pointer-events: none;
		@include create-circle(26px, 26px, $white, $form-input-border);
		@include small-shadow;
		@include quick-animation;
		i {
			@include vertical-horizontal-center-with-transform;
		}
	}

	&.toggle--condensed {
		.toggle__slider {
			width: 34px;
			height: 20px;
			border-radius: 11px;
			position: absolute;
			left: 0;
			right: initial;
			top: 0px;
			font-weight: 600;
			font-size: 10px;
			text-align: center;
			padding: $padding-xs $padding-xs;

			.option1 {
				@include vertical-center-with-transform;
				@include quick-animation;
				opacity: 0;
			}

			.option2 {
				@include quick-animation;
				opacity: 1;
			}
		}

		.toggle__input {
			height: 12px;
			width: 54px;
			min-width: 54px;

			&:checked {
				background-color: $white;
				color: $black;
				border-color: $ui-disable; //check

				+ .toggle__slider {
					background-color: $accent;
					border-color: transparent;
					color: $black;
					left: initial;
					right: 0;
					

					.option1 {
						@include quick-animation;
						opacity: 1;
					}

					.option2 {
						@include quick-animation;
						opacity: 0;
					}
				}
			}

			&:active,
			&:focus {
				&::before {
					width: 54px;
					height: 14px;
				}
			}
		}
	}
}

.toggle__input {
	&:checked {
		border: $form-input-border--checked;
		background-color: $accent;
		&:active,
		&:focus {
			background-color: $accent;
		}
		+ .toggle__slider {
			left: 28px; //create-check-element mixin width value minus slider width
		}
	}
	&:hover::after {
		border: $form-input-border-hover;
	}
}
</style>